import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { resolveCtaTarget } from './ctaTargets';

// Card 119 — the renderer for the served block schema.
//
// The schema is a small closed set rather than markdown or HTML: React Native
// cannot render arbitrary HTML, and markdown-with-tags would be an XSS surface
// here. Every block type the API can send has a case below; an unknown type
// renders nothing rather than throwing, so a backend that ships a new block
// before this build knows about it degrades instead of blanking the page.

const CALLOUT_TONES = {
  info: 'bg-indigo-50 border-indigo-100 text-indigo-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  success: 'bg-emerald-50 border-emerald-100 text-emerald-900'
};

// List items carry an optional bold lead-in so content can emphasise the first
// few words without smuggling markdown through the schema.
const ListItemText = ({ item }) => (
  <>
    {item.lead && <span className="font-bold text-slate-900">{item.lead} </span>}
    {item.text}
  </>
);

ListItemText.propTypes = {
  item: PropTypes.shape({ lead: PropTypes.string, text: PropTypes.string }).isRequired
};

const Block = ({ block }) => {
  const history = useHistory();

  switch (block.type) {
    case 'paragraph':
      return <p className="text-base text-slate-600 leading-relaxed m-0 max-w-[68ch]">{block.text}</p>;

    case 'heading':
      return <h3 className="text-base font-bold text-slate-900 m-0 mt-2">{block.text}</h3>;

    case 'steps':
      return (
        <ol className="list-none p-0 m-0 flex flex-col gap-3 max-w-[68ch]">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 items-start">
              <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <span className="text-base text-slate-600 leading-relaxed">
                <ListItemText item={item} />
              </span>
            </li>
          ))}
        </ol>
      );

    case 'bullets':
      return (
        <ul className="list-none p-0 m-0 flex flex-col gap-3 max-w-[68ch]">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 items-start">
              <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-slate-300 mt-[0.6rem]" />
              <span className="text-base text-slate-600 leading-relaxed">
                <ListItemText item={item} />
              </span>
            </li>
          ))}
        </ul>
      );

    case 'callout':
      return (
        <div
          className={`rounded-2xl border border-solid p-4 text-base leading-relaxed max-w-[68ch] ${CALLOUT_TONES[block.tone] ||
            CALLOUT_TONES.info}`}
        >
          {block.text}
        </div>
      );

    // Wording the guide can say out loud, verbatim. Styled as a pull quote so it
    // is unmistakable which words are ours and which are theirs to use — this is
    // the highest-value content in the library and it has to look quotable.
    case 'script':
      return (
        <blockquote className="m-0 border-0 border-l-4 border-solid border-indigo-300 bg-slate-50 rounded-r-2xl pl-5 pr-4 py-4 max-w-[68ch]">
          {block.label && (
            <div className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2">{block.label}</div>
          )}
          {block.lines.map((line, i) => (
            <p key={i} className="text-base text-slate-700 italic leading-relaxed m-0">
              &ldquo;{line}&rdquo;
            </p>
          ))}
        </blockquote>
      );

    case 'cta': {
      const route = resolveCtaTarget(block.target);
      if (!route) return null;
      return (
        <button
          type="button"
          onClick={() => history.push(route)}
          className="self-start bg-indigo-600 hover:bg-indigo-700 active:translate-y-px text-white text-sm font-bold border-0 rounded-xl px-5 py-3 cursor-pointer transition-colors"
        >
          {block.label}
        </button>
      );
    }

    default:
      return null;
  }
};

Block.propTypes = { block: PropTypes.object.isRequired };

const LearnBlocks = ({ blocks }) => (
  <div className="flex flex-col gap-4">
    {blocks.map((block, i) => (
      <Block key={i} block={block} />
    ))}
  </div>
);

LearnBlocks.propTypes = { blocks: PropTypes.array.isRequired };

export default LearnBlocks;
